// Avatar (DESIGN §3.8.6, §4.5): rund, Bild oder Initialen auf neutraler Fläche (N6), Badge-Slot unten rechts,
// Präsenz-Punkt und Status-Ring. Liegt in kern, weil steuerung ihn in Auswahllisten braucht.
import { type ComponentPropsWithRef, type ReactNode, useState } from 'react';
import { AVATAR_FILLS, avatarTone, initialsOf } from './avatar-palette';
import { cn } from './cn';
import { cva } from './cva';

export type AvatarSize = 20 | 24 | 28 | 32 | 36 | 40 | 52 | 56 | 64 | 96;
export type AvatarPresence = 'online' | 'away' | 'offline';
export type AvatarRing = 'alert' | 'accent' | 'grade-a' | 'grade-above' | 'grade-under';
/** Untergrund, damit Ringe um Badge und Avatar die Fläche „ausstanzen“. */
export type AvatarSurface = 'page' | 'card' | 'stripe' | 'tile' | 'overlay' | 'selection';

const SIZE_CLASS: Record<AvatarSize, string> = {
  20: 'size-5',
  24: 'size-6',
  28: 'size-7',
  32: 'size-8',
  36: 'size-9',
  40: 'size-10',
  52: 'size-13',
  56: 'size-14',
  64: 'size-16',
  96: 'size-24',
};

const ringVariants = cva('ring-2 ring-offset-2', {
  variants: {
    ring: {
      alert: 'ring-ring-alert',
      accent: 'ring-accent',
      'grade-a': 'ring-grade-a',
      'grade-above': 'ring-grade-above',
      'grade-under': 'ring-grade-under',
    },
    surface: {
      page: 'ring-offset-page',
      card: 'ring-offset-card',
      stripe: 'ring-offset-stripe',
      tile: 'ring-offset-tile',
      overlay: 'ring-offset-overlay',
      selection: 'ring-offset-selection',
    },
  },
});

const SURFACE_RING: Record<AvatarSurface, string> = {
  page: 'ring-page',
  card: 'ring-card',
  stripe: 'ring-stripe',
  tile: 'ring-tile',
  overlay: 'ring-overlay',
  selection: 'ring-selection',
};

const PRESENCE: Record<AvatarPresence, { className: string; label: string }> = {
  online: { className: 'bg-presence-online', label: 'Online' },
  away: { className: 'bg-presence-away', label: 'Away' },
  offline: { className: 'bg-presence-offline', label: 'Offline' },
};

export interface AvatarProps extends Omit<ComponentPropsWithRef<'span'>, 'children'> {
  /** Anzeigename; daraus Initialen und Alt-Text. */
  name: string;
  /** Stabile ID für die Verlaufsfarbe (Personen-ID); ohne Angabe der Name. */
  id?: string;
  src?: string | null;
  size?: AvatarSize;
  /** `square`: Monogramm-Kachel (Teams, Apps, Bot). */
  shape?: 'circle' | 'square';
  presence?: AvatarPresence;
  /** Inhalt des Badge-Slots unten rechts (Status-Icon, Häkchen, Funkeln). */
  badge?: ReactNode;
  ring?: AvatarRing;
  surface?: AvatarSurface;
  /** Steht der Name daneben, ist der Avatar dekorativ. */
  decorative?: boolean;
  loading?: boolean;
}

export function Avatar({
  name,
  id,
  src,
  size = 40,
  shape = 'circle',
  presence,
  badge,
  ring,
  surface = 'page',
  decorative,
  loading,
  className,
  ...rest
}: AvatarProps) {
  const [failed, setFailed] = useState(false);
  const rounded = shape === 'circle' ? 'rounded-full' : size <= 24 ? 'rounded-sm' : 'rounded-md';
  const sizeClass = SIZE_CLASS[size];
  if (loading) {
    return (
      <span
        aria-hidden
        className={cn(
          'inline-block shrink-0 animate-skeleton bg-skeleton',
          rounded,
          sizeClass,
          className,
        )}
      />
    );
  }
  const showImage = Boolean(src) && !failed;
  const badgeSize = Math.max(8, Math.round(size * 0.4));
  const label =
    presence && !decorative ? `${name}, ${PRESENCE[presence].label.toLowerCase()}` : name;
  return (
    <span
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : label}
      aria-hidden={decorative ? true : undefined}
      data-size={size}
      className={cn('relative inline-flex shrink-0', sizeClass, className)}
      {...rest}
    >
      <span
        className={cn(
          'flex size-full items-center justify-center overflow-hidden font-semibold text-avatar-ink select-none',
          rounded,
          !showImage && AVATAR_FILLS[avatarTone(id ?? name)],
          ring && ringVariants({ ring, surface }),
        )}
        style={{ fontSize: Math.round(size * 0.4) }}
      >
        {showImage ? (
          <img
            src={src ?? undefined}
            alt=""
            className="size-full object-cover"
            onError={() => setFailed(true)}
          />
        ) : (
          <span aria-hidden>{initialsOf(name)}</span>
        )}
      </span>
      {presence || badge ? (
        <span
          aria-hidden
          data-slot="avatar-badge"
          className={cn(
            'absolute -right-0.5 -bottom-0.5 flex items-center justify-center overflow-hidden rounded-full ring-2',
            SURFACE_RING[surface],
            presence && !badge ? PRESENCE[presence].className : undefined,
          )}
          style={{ width: badgeSize, height: badgeSize }}
        >
          {badge}
        </span>
      ) : null}
    </span>
  );
}
